import React, { useState, useRef, useEffect } from "react";
import "./CreditsModal.scss";
import i18n from "i18n/config";

import Credits1A from "../../assets/graphics/credits/credits-1a.png";
import Credits1B from "../../assets/graphics/credits/credits-1b.png";
import Credits2A from "../../assets/graphics/credits/credits-2a.png";
import Credits2B from "../../assets/graphics/credits/credits-2b.png";
import Credits3A from "../../assets/graphics/credits/credits-3a.png";
import Credits3B from "../../assets/graphics/credits/credits-3b.png";

import fncfcsEn from "../../assets/graphics/credits/FNCFCS-logo-en.png";
import fncfcsFr from "../../assets/graphics/credits/FNCFCS-logo-fr.png";
import sfpLogo from "../../assets/graphics/credits/SFP-logo.png";
import steamlabsLogo from "../../assets/graphics/credits/SteamLabs-logo.png";
import ccaEn from "../../assets/graphics/credits/CCA-logo-en.png";
import ccaFr from "../../assets/graphics/credits/CCA-logo-fr.png";

interface LocalizedName {
  en: string;
  fr: string;
}

interface CreditEntry {
  role_en: string;
  role_fr: string;
  names: (string | LocalizedName)[];
  /** Optional extra note shown below the names (e.g. composer credit) */
  note_en?: string;
  note_fr?: string;
}

interface CreditsModalProps {
  onClose?: () => void;
}

// ─── Credits data ────────────────────────────────────────────────────────────

const CREDIT_ENTRIES: CreditEntry[] = [
  {
    role_en: "Director",
    role_fr: "Directrice",
    names: ["Amanda Strong"],
  },
  {
    role_en: "Executive Producer",
    role_fr: "Productrice exécutive",
    names: ["Cindy Blackstock"],
  },
  {
    role_en: "Senior Producers",
    role_fr: "Productrices principales",
    names: ["Melisa Brittain", "Jennifer King", "Jessica Raby"],
  },
  {
    role_en: "Producers",
    role_fr: "Productrices",
    names: ["Amanda Strong", "Nina Werewka"],
  },
  {
    role_en: "Production Managers",
    role_fr: "Direction de production",
    names: [
      "Melisa Brittain",
      "Jennifer King",
      "Brenda Shivanandan",
      "Mark Robert Whiting",
    ],
  },
  {
    role_en: "Technical Director",
    role_fr: "Direction technique",
    names: ["Andy Forest"],
  },
  {
    role_en: "Lead Developer",
    role_fr: "Responsable du développement",
    names: ["Maho Morinaga"],
  },
  {
    role_en: "Technical Supervisor",
    role_fr: "Supervision technique",
    names: ["Keith Morgan"],
  },
  {
    role_en: "Designers",
    role_fr: "Conception",
    names: ["Harlen Bertrand", "Lia Fabre-Dimsdale"],
  },
  {
    role_en: "3D Model Lead",
    role_fr: "Direction de la modélisation 3D",
    names: ["Jhoao Cundari"],
  },
  {
    role_en: "3D Modeler",
    role_fr: "Modélisation 3D",
    names: ["Harlen Bertrand"],
  },
  {
    role_en: "3D Animation",
    role_fr: "Animation 3D",
    names: ["Harlen Bertrand", "Alena Wagner"],
  },
  {
    role_en: "Rigger",
    role_fr: "Gréage",
    names: ["Alena Wagner"],
  },
  {
    role_en: "UI/UX Designer",
    role_fr: "Conception UI/UX",
    names: ["Brenda Shivanandan"],
  },
  {
    role_en: "Creative Consultant",
    role_fr: "Consultant en création",
    names: ["Skekem Xikst Hanuse"],
  },
  {
    role_en: "Graphic Design",
    role_fr: "Conception graphique",
    names: ["Leah Gryfe"],
  },
  {
    role_en: "Script Writers",
    role_fr: "Scénarisation",
    names: [
      "Cindy Blackstock",
      "Melisa Brittain",
      "Bracken Hanuse Corlett",
      "Jennifer King",
      "Jessica Raby",
    ],
  },
  {
    role_en: "Character Voices",
    role_fr: "Voix des personnages",
    names: [
      "Spirit Bear – Theland Kicknosway",
      "Mary the Bear – Mary Teegee",
      { en: "Voice Over – Mary Teegee", fr: "Voix hors champ – Mary Teegee" },
    ],
  },
  {
    role_en: "Recording Engineers",
    role_fr: "Ingénieurs de son",
    names: ["Jim Gardner", "Stephen St Laurent"],
  },
  {
    role_en: "Music",
    role_fr: "Musique",
    note_en: "All tracks Written and Performed by Matthew Cardinal",
    note_fr: "Toutes les pièces écrites et interprétées par Matthew Cardinal",
    names: [
      '"SB pizzicato and synth-mix 2"',
      '"Spirit Bear BGM 15 morning in the woods"',
      '"Spirit Bear BGM13 protest"',
      '"SB twinklings wurli ob"',
      '"SB fishing fun wurliedit"',
      '"SB twinklings wurli delay zen"',
      '"SB BGM9 ending scene"',
    ],
  },
  {
    role_en: "Photos",
    role_fr: "Photos",
    names: [
      {
        en: "Jordan River Anderson – National Film Board of Canada",
        fr: "Jordan River Anderson – Office national du film du Canada",
      },
      {
        en: "Spirit Bear on the set of his first movie – James Doyle",
        fr: "Spirit Bear sur le plateau de tournage de son premier film - James Doyle",
      },
    ],
  },
];

/**
 * Image pairs shown in the sticky sidebar — one pair per "visible page"
 * (each time the user scrolls one viewport-height worth of content).
 * Replace null with an image path string when artwork is available.
 */
const IMAGE_PAGES: Array<[string | null, string | null]> = [
  [Credits1A, Credits1B],
  [Credits2A, Credits2B],
  [Credits3A, Credits3B],
];

/**
 * Single images shown on mobile — one per scroll segment (6 total).
 * Reuse the uploaded sidebar artwork so small screens still show images.
 */
const MOBILE_IMAGES: Array<string | null> = IMAGE_PAGES.flat();

// ─── Component ────────────────────────────────────────────────────────────────

const CreditsModal = ({ onClose }: CreditsModalProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activePage, setActivePage] = useState(0);
  const [mobileActivePage, setMobileActivePage] = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 600);
  const [isShortHeight, setIsShortHeight] = useState(
    () => window.innerHeight < 350,
  );
  const lang = i18n.language;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
      setIsShortHeight(window.innerHeight < 350);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, clientHeight, scrollHeight } = scrollRef.current;

    const maxScroll = scrollHeight - clientHeight;
    const page =
      maxScroll > 0
        ? Math.min(
            Math.floor((scrollTop / maxScroll) * IMAGE_PAGES.length),
            IMAGE_PAGES.length - 1,
          )
        : 0;
    setActivePage((prev) => (prev !== page ? page : prev));

    const mobilePage =
      maxScroll > 0
        ? Math.min(
            Math.max(
              0,
              Math.floor((scrollTop / maxScroll) * MOBILE_IMAGES.length),
            ),
            MOBILE_IMAGES.length - 1,
          )
        : 0;
    setMobileActivePage((prev) => (prev !== mobilePage ? mobilePage : prev));
  };

  const [img1, img2] = IMAGE_PAGES[activePage];
  const mobileImg = MOBILE_IMAGES[mobileActivePage];
  const useSingleImage = isMobile || isShortHeight;

  return (
    <div className="credits-modal" onClick={onClose}>
      <div
        className="credits-modal__content"
        onClick={(e) => e.stopPropagation()}
      >
        {onClose && (
          <button
            className="credits-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        )}

        {/* ── Two-column body: sticky image sidebar (left) + scrollable text (right) ── */}
        <div className="credits-modal__body">
          {/* ── Sticky image sidebar ── */}
          <div className="credits-modal__sidebar">
            <div
              className={`credits-modal__sidebar-images${useSingleImage ? " credits-modal__sidebar-images--single" : ""}`}
              key={useSingleImage ? mobileActivePage : activePage}
            >
              <div className="credits-modal__sidebar-img credits-modal__sidebar-img--back">
                {(useSingleImage ? mobileImg : img1) ? (
                  <img src={(useSingleImage ? mobileImg : img1)!} alt="" />
                ) : (
                  <div className="credits-modal__img-placeholder" />
                )}
              </div>
              <div
                className={`credits-modal__sidebar-img credits-modal__sidebar-img--front${useSingleImage ? " credits-modal__sidebar-img--hidden" : ""}`}
              >
                {img2 ? (
                  <img src={img2} alt="" />
                ) : (
                  <div className="credits-modal__img-placeholder" />
                )}
              </div>
            </div>
          </div>

          <div
            className="credits-modal__scroll"
            ref={scrollRef}
            onScroll={handleScroll}
          >
            {CREDIT_ENTRIES.map((entry, i) => (
              <div key={i} className="credits-modal__row">
                <span className="credits-modal__role">
                  {lang === "fr" ? entry.role_fr : entry.role_en}
                </span>

                {entry.note_en && (
                  <span className="credits-modal__note">
                    {lang === "fr" ? entry.note_fr : entry.note_en}
                  </span>
                )}

                <ul className="credits-modal__names">
                  {entry.names.map((name, j) => (
                    <li key={j}>
                      {typeof name === "string"
                        ? name
                        : lang === "fr"
                          ? name.fr
                          : name.en}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* ── Special Thanks ── */}
            <div className="credits-modal__special-thanks">
              <p>
                {lang === "fr"
                  ? "Remerciements spéciaux aux enfants qui ont commenté la première version de Bear Shares, lancée en 2024."
                  : "Special thanks to the children who provided feedback on the first version of Bear Shares, launched in 2024."}
              </p>
            </div>

            {/* ── Logos ── */}
            <div className="credits-modal__logos">
              <div className="credits-modal__logo">
                <img
                  src={lang === "fr" ? fncfcsFr : fncfcsEn}
                  alt={
                    lang === "fr"
                      ? "Société de soutien à l'enfance et à la famille des Premières Nations"
                      : "First Nations Child & Family Caring Society"
                  }
                />
              </div>
              <div className="credits-modal__logo">
                <img src={sfpLogo} alt="Spotted Fawn Productions" />
              </div>
              <div className="credits-modal__logo">
                <img src={steamlabsLogo} alt="Steamlabs" />
              </div>
            </div>

            {/* ── Canada Council for the Arts ── */}
            <div className="credits-modal__cca">
              <p>
                {lang === "fr"
                  ? "Nous remercions le Conseil des arts du Canada de son soutien"
                  : "We acknowledge the support of the Canada Council for the Arts"}
              </p>
              <div className="credits-modal__logo">
                <img
                  src={lang === "fr" ? ccaFr : ccaEn}
                  alt={
                    lang === "fr"
                      ? "Conseil des arts du Canada"
                      : "Canada Council for the Arts"
                  }
                />
              </div>
            </div>

            {/* ── Copyright ── */}
            <div className="credits-modal__copyright">
              {lang === "fr"
                ? "© 2026 Société de soutien à l'enfance et à la famille des Premières Nations du Canada"
                : "© First Nations Child & Family Caring Society of Canada"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditsModal;
